import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import PlannerIcon from "@/components/appointmentRequest/assignmentPlanner/plannerIcon";
import {
  AssignmentPlan,
  canMoveEmployee,
  PlannerEmployee,
  PlannerSlot,
  slotDescription,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import PlannerEmployeeIdentity from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeIdentity";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

export interface EmployeeRowData {
  employees: PlannerEmployee[];
  plan: AssignmentPlan;
  duplicateIds: Set<string>;
  slots: Map<string, PlannerSlot>;
  onChoose: (employee: PlannerEmployee) => void;
  withActions: boolean;
  isDisabled?: (employee: PlannerEmployee) => boolean;
}

export default function PlannerEmployeeRow({
  index,
  style,
  data,
}: ListChildComponentProps<EmployeeRowData>) {
  const { t } = useTranslation();
  const {
    employees,
    plan,
    duplicateIds,
    slots,
    onChoose,
    withActions,
    isDisabled,
  } = data;
  const employee = employees[index];
  const slotId = plan[employee.id];
  const duplicateName = duplicateIds.has(employee.id);
  const showIdentifiers = Boolean(
    employee.worker_id || (duplicateName && employee.email),
  );
  const actionIdentity = duplicateName
    ? [employee.name, employee.worker_id, employee.email]
        .filter(Boolean)
        .join(" · ")
    : employee.name;
  const booking = slotId ? slotDescription(slots.get(slotId)) : undefined;
  return (
    <div
      role="listitem"
      style={style}
      data-employee-index={index}
      aria-posinset={index + 1}
      aria-setsize={employees.length}
    >
      <button
        type="button"
        className={`${styles.employee} ${slotId ? styles.assigned : ""} ${showIdentifiers ? styles.employeeWithIdentifiers : ""} ${withActions ? styles.employeeWithActions : ""}`}
        aria-label={
          withActions
            ? `${t(slotId ? "planner.reassign" : "planner.assign")} ${actionIdentity}`
            : undefined
        }
        disabled={!canMoveEmployee(employee) || Boolean(isDisabled?.(employee))}
        onClick={() => onChoose(employee)}
      >
        <PlannerEmployeeIdentity
          employee={employee}
          showEmail={duplicateName}
        />
        <small title={booking}>{booking}</small>
        {withActions && slotId && (
          <span
            className={`${styles.icon} ${styles.employeeActionIcon}`}
            aria-hidden="true"
          >
            <PlannerIcon name="pencil" />
          </span>
        )}
      </button>
    </div>
  );
}
